import React from "react";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { SERVICES_DATA } from "../../data/servicesData";
import styleVariables from "../../variables.module.scss";

const ServicesTable: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const MobileServiceCard = ({ service }: { service: any }) => (
    <Card
      sx={{
        mb: 2,
        boxShadow: 2,
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)",
          transition: "all 0.3s ease-in-out",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h6"
          component="h3"
          gutterBottom
          sx={{
            color: styleVariables.themeGreyColor,
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {service.name}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.6 }}
        >
          {service.description}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "bold",
              mb: 1,
              color: styleVariables.themeOrangeColor,
            }}
          >
            Key Features:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {service.features
              .slice(0, 3)
              .map((feature: string, index: number) => (
                <Chip
                  key={index}
                  label={feature}
                  size="small"
                  sx={{
                    backgroundColor: styleVariables.themeOrangeColor,
                    color: "white",
                    fontSize: "0.75rem",
                    "&:hover": {
                      backgroundColor: theme.palette.custom.status.hover,
                    },
                  }}
                />
              ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 1,
            borderTop: `1px solid ${theme.palette.custom.border.light}`,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Service Area: Ottawa & Surrounding Areas
          </Typography>
          <Chip
            label="Licensed"
            size="small"
            sx={{
              backgroundColor: theme.palette.custom.status.success,
              color: "white",
              fontSize: "0.7rem",
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );

  const DesktopTable = () => (
    <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: styleVariables.themeOrangeColor }}>
            <TableCell sx={{ fontWeight: "bold", color: "black" }}>
              Service
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "black" }}>
              Description
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "black" }}>
              Key Features
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "black" }}>
              Service Area
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.values(SERVICES_DATA).map((service) => (
            <TableRow
              key={service.slug}
              sx={{
                "&:nth-of-type(odd)": {
                  backgroundColor: theme.palette.custom.background.card,
                },
              }}
            >
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: styleVariables.themeGreyColor,
                }}
              >
                {service.name}
              </TableCell>
              <TableCell sx={{ maxWidth: "300px" }}>
                {service.description}
              </TableCell>
              <TableCell>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {service.features
                    .slice(0, 3)
                    .map((feature: string, index: number) => (
                      <li key={index} style={{ marginBottom: "4px" }}>
                        {feature}
                      </li>
                    ))}
                </ul>
              </TableCell>
              <TableCell>Ottawa & Surrounding Areas</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ py: 8, backgroundColor: theme.palette.custom.background.light }}>
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: styleVariables.themeGreyColor,
            fontWeight: "bold",
            mb: 4,
            fontSize: { xs: "1.75rem", sm: "2.125rem", md: "3rem" },
          }}
        >
          Our Services Overview
        </Typography>
        <Typography
          variant="body1"
          textAlign="center"
          sx={{
            fontSize: { xs: "1rem", md: "1.1rem" },
            lineHeight: 1.7,
            mb: 6,
            maxWidth: "800px",
            mx: "auto",
            px: { xs: 2, md: 0 },
          }}
        >
          Comprehensive handyman services in Ottawa and surrounding areas. Each
          service is performed by licensed professionals with years of
          experience.
        </Typography>

        {isMobile ? (
          <Box sx={{ px: { xs: 1, sm: 2 } }}>
            {Object.values(SERVICES_DATA).map((service) => (
              <MobileServiceCard key={service.slug} service={service} />
            ))}
          </Box>
        ) : (
          <DesktopTable />
        )}

        <Box sx={{ mt: 4, textAlign: "center", px: { xs: 2, md: 0 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            * All services include free estimates and consultations
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            * Licensed and insured professionals (with certified partners for
            specialized work)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            * Satisfaction guaranteed on all work
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesTable;
